import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import VehicleLayout from '../../components/VehicleLayout';
import { useAxios } from '../../utils/hook/useAxios';
import './mc-vehicle-pages.css';

/* ---------- Données d'affichage ---------- */

const PREAVIS_OPTIONS = [
  { value: '0', label: '1 heure' },
  { value: '1', label: '2 heures' },
  { value: '2', label: '3 heures' },
  { value: '3', label: '6 heures' },
  { value: '4', label: '12 heures' },
  { value: '5', label: '1 jour' },
  { value: '6', label: '2 jours' },
];

const BUFFER_OPTIONS = [
  { value: '0', label: '3 heures' },
  { value: '1', label: '4 heures' },
  { value: '2', label: '5 heures' },
  { value: '3', label: '6 heures' },
  { value: '4', label: '7 heures' },
  { value: '5', label: '8 heures' },
  { value: '6', label: '12 heures' },
  { value: '7', label: '1 jour' },
  { value: '8', label: '2 jours' },
  { value: '9', label: '3 jours' },
];

const MIN_DURATION_OPTIONS = [
  { value: '0', label: 'Peu importe' },
  { value: '1', label: '1 jour' },
  { value: '2', label: '2 jours' },
  { value: '3', label: '3 jours' },
  { value: '4', label: '5 jours' },
];

const MAX_DURATION_OPTIONS = [
  { value: '0', label: '5 jours' },
  { value: '1', label: '1 semaine' },
  { value: '2', label: '2 semaines' },
  { value: '3', label: '1 mois' },
  { value: '4', label: '3 mois' },
  { value: '5', label: 'Peu importe' },
];

const CANCEL_POLICIES = [
  {
    value: 'flexible',
    label: 'Flexible',
    desc: '100% remboursé si annulation >24h avant, 50% sinon',
  },
  {
    value: 'moderate',
    label: 'Modérée',
    desc: '100% si >5 jours avant, 50% si 1–5 jours, 0% sinon',
  },
  {
    value: 'strict',
    label: 'Stricte',
    desc: '100% si >14 jours avant, non remboursable sinon',
  },
];

// Alertes affichées en haut de page (contenu inchangé, à supprimer ou rendre dynamique)
const NOTICES = [
  {
    tone: 'danger',
    title: 'Action requise : activer la Réservation instantanée',
    text: 'L’annonce pour votre véhicule risque d’être désactivée à cette date à moins que vous activiez la Réservation instantanée pour votre domicile.',
  },
  {
    tone: 'warning',
    title: 'Action requise : activer la Réservation instantanée',
    text: 'Les lieux de livraison risquent d’être désactivés à cette date à moins que vous activiez la Réservation instantanée pour vos livraisons.',
  },
];

/* ---------- Petits composants de présentation ---------- */

function SectionCard({ icon, title, desc, tone, children }) {
  return (
    <section className="mc-form-card mc-vp-card">
      <header className="mc-vp-card-head">
        <span
          className={`mc-vp-card-icon${tone ? ` mc-vp-card-icon--${tone}` : ''}`}
          aria-hidden="true"
        >
          <i className={`bi bi-${icon}`}></i>
        </span>
        <div>
          <h2 className="mc-vp-card-title">{title}</h2>
          {desc && <p className="mc-vp-card-desc">{desc}</p>}
        </div>
      </header>
      {children}
    </section>
  );
}

function SelectField({ id, label, value, onChange, options }) {
  return (
    <div className="mc-vp-field">
      <label className="mc-field-label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="mc-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function TimePair({ idPrefix, icon, title, startValue, endValue, onStart, onEnd }) {
  return (
    <div className="mc-vp-panel" role="group" aria-labelledby={`${idPrefix}-title`}>
      <h4 className="mc-vp-panel-title" id={`${idPrefix}-title`}>
        <i className={`bi bi-${icon}`} aria-hidden="true"></i>
        {title}
      </h4>
      <div className="mc-vp-times">
        <div>
          <label className="mc-field-label" htmlFor={`${idPrefix}-start`}>
            Ouverture
          </label>
          <input
            id={`${idPrefix}-start`}
            type="time"
            className="mc-input"
            value={startValue}
            onChange={(e) => onStart(e.target.value)}
          />
        </div>
        <div>
          <label className="mc-field-label" htmlFor={`${idPrefix}-end`}>
            Fermeture
          </label>
          <input
            id={`${idPrefix}-end`}
            type="time"
            className="mc-input"
            value={endValue}
            onChange={(e) => onEnd(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */

function PreferencesReservation() {
  const axios = useAxios();
  const userData = JSON.parse(window.localStorage.getItem('me'));
  const { vehiculeId, mois } = useParams();
  const [HasReservationInstantanne, setHasReservationInstantanne] = useState(false);
  const [HomePreavis, setHomePreavis] = useState('0');
  const [HomePeriodTempo, setHomePeriodTempo] = useState('0');
  const [HomeMinDureeVoyage, setHomeMinDureeVoyage] = useState('0');
  const [HomeMaxDureeVoyage, setHomeMaxDureeVoyage] = useState('0');

  const [LocationDeliveryPreavis, setLocationDeliveryPreavis] = useState('0');
  const [LocationDeliveryMinDureeVoyage, setLocationDeliveryMinDureeVoyage] = useState('0');
  const [LocationDeliveryLocationMaxDureeVoyage, setLocationDeliveryLocationMaxDureeVoyage] =
    useState('0');
  const [LocationDeliveryPeriodTempo, setLocationDeliveryPeriodTempo] = useState('0');

  // Heures de prise en charge / retour + politique annulation (stockées sur l'annonce)
  const [annonceId, setAnnonceId] = useState(null);
  const [pickupTimeStart, setPickupTimeStart] = useState('08:00');
  const [pickupTimeEnd, setPickupTimeEnd] = useState('20:00');
  const [returnTimeStart, setReturnTimeStart] = useState('08:00');
  const [returnTimeEnd, setReturnTimeEnd] = useState('20:00');
  const [annulationPolicy, setAnnulationPolicy] = useState('moderate');

  useEffect(() => {
    let fetchReviews = async () => {
      try {
        const response = await axios.get('/reservationpreferences/getbyvehiculeid/' + vehiculeId);
        if (response.data) {
          setHasReservationInstantanne(response.data.reservationsInstantanne);
          setHomePreavis(response.data.home_preavis);
          setHomePeriodTempo(response.data.HomePeriodTempo);
          setHomeMinDureeVoyage(response.data.home_minDureeVoyage);
          setHomeMaxDureeVoyage(response.data.home_maxDureeVoyage);
          setLocationDeliveryPreavis(response.data.locationDelivery_preavis);
          setLocationDeliveryPeriodTempo(response.data.locationDeliveryPeriodTempo);
          setLocationDeliveryMinDureeVoyage(response.data.locationDelivery_minDureeVoyage);
          setLocationDeliveryLocationMaxDureeVoyage(response.data.locationDelivery_maxDureeVoyage);
        }
      } catch (error) {
        console.error('Erreur lors de la requête Axios :', error);
      }
    };
    fetchReviews();
  }, []);

  // Charger les heures depuis l'annonce existante
  useEffect(() => {
    axios
      .get('/vehicleAnnonces/getbyvehiculeid/' + vehiculeId)
      .then(({ data }) => {
        if (data?.length > 0) {
          const a = data[0];
          setAnnonceId(a.vehiculeAnnonceId);
          if (a.pickupTimeStart) setPickupTimeStart(a.pickupTimeStart);
          if (a.pickupTimeEnd) setPickupTimeEnd(a.pickupTimeEnd);
          if (a.returnTimeStart) setReturnTimeStart(a.returnTimeStart);
          if (a.returnTimeEnd) setReturnTimeEnd(a.returnTimeEnd);
          if (a.annulationPolicy) setAnnulationPolicy(a.annulationPolicy);
        }
      })
      .catch(() => {});
  }, [vehiculeId]);

  async function handleSavePickupTimes() {
    if (!annonceId) return;
    try {
      await axios.put(`/vehicleAnnonces/${annonceId}`, {
        pickupTimeStart,
        pickupTimeEnd,
        returnTimeStart,
        returnTimeEnd,
        annulationPolicy,
      });
      toast.success('Heures de prise en charge enregistrées');
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  }

  function handleReservationsPreferencesSetting() {
    let fetchReviews = async () => {
      try {
        const response = await axios.put('/reservationpreferences/' + vehiculeId, {
          reservationsInstantanne: HasReservationInstantanne,
          home_preavis: HomePreavis,
          home_minDureeVoyage: HomeMinDureeVoyage,
          home_maxDureeVoyage: HomeMaxDureeVoyage,
          locationDelivery_preavis: LocationDeliveryPreavis,
          locationDelivery_minDureeVoyage: LocationDeliveryMinDureeVoyage,
          locationDelivery_maxDureeVoyage: LocationDeliveryLocationMaxDureeVoyage,
          locationDeliveryPeriodTempo: LocationDeliveryPeriodTempo,
          HomePeriodTempo: HomePeriodTempo,
        });

        if (response.data) {
          toast.success('Vos paramètres de reservations ont été modifiés avec succès');
        }
      } catch (error) {
        console.error('Erreur lors de la requête Axios :', error);
      }
    };
    fetchReviews();
  }

  return (
    <VehicleLayout vehicleId={vehiculeId} pageTitle="Préférences">
      <ToastContainer />
      <div className="mc-stack mc-vp-page">
        <header className="mc-page-head">
          <h1 className="mc-form-title mc-form-title--md">Préférences de réservation</h1>
          <p className="mc-form-subtitle">
            Choisissez comment et quand les locataires peuvent réserver votre véhicule.
          </p>
        </header>

        {/* ── Alertes ── */}
        {NOTICES.length > 0 && (
          <div className="mc-vp-notices">
            {NOTICES.map((n, i) => (
              <div
                key={i}
                className={`mc-vp-notice${n.tone === 'danger' ? ' mc-vp-notice--danger' : ''}`}
                role="alert"
              >
                <i
                  className={`bi ${n.tone === 'danger' ? 'bi-exclamation-octagon-fill' : 'bi-exclamation-triangle-fill'}`}
                  aria-hidden="true"
                ></i>
                <div className="mc-vp-notice-body">
                  <strong className="mc-vp-notice-title">{n.title}</strong>
                  <p className="mc-vp-notice-text">{n.text}</p>
                  <a href="#" className="mc-vp-notice-link">
                    En savoir plus
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Réservation : domicile + livraison ── */}
        <section className="mc-form-card mc-vp-card" aria-labelledby="pref-resa-title">
          <header className="mc-vp-card-head">
            <span className="mc-vp-card-icon" aria-hidden="true">
              <i className="bi bi-lightning-charge"></i>
            </span>
            <div>
              <h2 className="mc-vp-card-title" id="pref-resa-title">
                Délais et durées
              </h2>
              <p className="mc-vp-card-desc">
                Préavis, période tampon et durée de voyage, selon le lieu de remise du véhicule.
              </p>
            </div>
          </header>

          <div className="mc-vp-group">
            <div className="mc-vp-group-head">
              <h3 className="mc-vp-group-title">Domicile</h3>
              <span className="mc-vp-place">
                <i className="bi bi-geo-alt-fill" aria-hidden="true"></i>
                Rue de Provence, Échirolles, FR 38130
              </span>
            </div>

            <div className="mc-vp-switch-wrap">
              <label className="mc-switch" htmlFor="reservationInstantaneCheck">
                <input
                  type="checkbox"
                  role="switch"
                  id="reservationInstantaneCheck"
                  checked={!!HasReservationInstantanne}
                  onChange={() => setHasReservationInstantanne(!HasReservationInstantanne)}
                />
                <span className="mc-switch-text">Réservation instantanée</span>
                <span className="mc-switch-track" aria-hidden="true"></span>
              </label>
              <p className="mc-vp-switch-note">
                Les locataires réservent sans attendre votre validation.
              </p>
            </div>

            <div className="mc-vp-fields mc-vp-fields--spaced">
              <SelectField
                id="home-preavis"
                label="Préavis"
                value={HomePreavis}
                onChange={setHomePreavis}
                options={PREAVIS_OPTIONS}
              />
              <SelectField
                id="home-buffer"
                label="Période tampon"
                value={HomePeriodTempo}
                onChange={setHomePeriodTempo}
                options={BUFFER_OPTIONS}
              />
              <SelectField
                id="home-min"
                label="Durée de voyage minimale"
                value={HomeMinDureeVoyage}
                onChange={setHomeMinDureeVoyage}
                options={MIN_DURATION_OPTIONS}
              />
              <SelectField
                id="home-max"
                label="Durée de voyage maximale"
                value={HomeMaxDureeVoyage}
                onChange={setHomeMaxDureeVoyage}
                options={MAX_DURATION_OPTIONS}
              />
            </div>
          </div>

          <div className="mc-vp-group">
            <div className="mc-vp-group-head">
              <h3 className="mc-vp-group-title">Lieux de livraison</h3>
            </div>

            <div className="mc-vp-fields">
              <SelectField
                id="delivery-preavis"
                label="Préavis"
                value={LocationDeliveryPreavis}
                onChange={setLocationDeliveryPreavis}
                options={PREAVIS_OPTIONS}
              />
              <SelectField
                id="delivery-buffer"
                label="Période tampon"
                value={LocationDeliveryPeriodTempo}
                onChange={setLocationDeliveryPeriodTempo}
                options={BUFFER_OPTIONS}
              />
              <SelectField
                id="delivery-min"
                label="Durée de voyage minimale"
                value={LocationDeliveryMinDureeVoyage}
                onChange={setLocationDeliveryMinDureeVoyage}
                options={MIN_DURATION_OPTIONS}
              />
              <SelectField
                id="delivery-max"
                label="Durée de voyage maximale"
                value={LocationDeliveryLocationMaxDureeVoyage}
                onChange={setLocationDeliveryLocationMaxDureeVoyage}
                options={MAX_DURATION_OPTIONS}
              />
            </div>
          </div>

          <div className="mc-vp-card-actions">
            <button
              type="button"
              className="mc-btn mc-btn--primary"
              onClick={handleReservationsPreferencesSetting}
            >
              Enregistrer
            </button>
          </div>
        </section>

        {/* ── Week-end ── */}
        <div className="mc-vp-note" role="note">
          <i className="bi bi-calendar-week" aria-hidden="true"></i>
          <div>
            <strong>Durée de voyage minimale sur un week-end</strong>
            <p>
              Les voyages débutant les vendredis, samedis ou dimanches doivent durer au moins 2
              jours.
            </p>
          </div>
        </div>

        {/* ── Horaires + annulation (enregistrés sur l'annonce) ── */}
        <SectionCard
          icon="clock"
          tone="teal"
          title="Horaires et annulation"
          desc="Créneaux pendant lesquels les locataires peuvent récupérer et rendre le véhicule, et conditions de remboursement."
        >
          <div className="mc-vp-panels">
            <TimePair
              idPrefix="pickup"
              icon="box-arrow-in-right"
              title="Prise en charge"
              startValue={pickupTimeStart}
              endValue={pickupTimeEnd}
              onStart={setPickupTimeStart}
              onEnd={setPickupTimeEnd}
            />
            <TimePair
              idPrefix="return"
              icon="box-arrow-left"
              title="Retour"
              startValue={returnTimeStart}
              endValue={returnTimeEnd}
              onStart={setReturnTimeStart}
              onEnd={setReturnTimeEnd}
            />
          </div>

          <div className="mc-vp-subsection">
            <h3 className="mc-vp-subsection-title">
              <i className="bi bi-shield-check" aria-hidden="true"></i>
              Politique d'annulation
            </h3>
            <div className="mc-policy-list" role="radiogroup" aria-label="Politique d'annulation">
              {CANCEL_POLICIES.map(({ value, label, desc }) => (
                <label key={value} className="mc-policy" htmlFor={`annulPolicy-${value}`}>
                  <input
                    type="radio"
                    id={`annulPolicy-${value}`}
                    name="annulPolicy"
                    value={value}
                    checked={annulationPolicy === value}
                    onChange={() => setAnnulationPolicy(value)}
                  />
                  <span className="mc-policy-body">
                    <strong>{label}</strong>
                    <small>{desc}</small>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="mc-vp-card-actions">
            {!annonceId && (
              <p className="mc-vp-hint">Aucune annonce liée à ce véhicule pour le moment.</p>
            )}
            <button
              type="button"
              className="mc-btn mc-btn--secondary"
              onClick={handleSavePickupTimes}
              disabled={!annonceId}
            >
              <i className="bi bi-floppy" aria-hidden="true"></i>
              Enregistrer les horaires
            </button>
          </div>
        </SectionCard>
      </div>
    </VehicleLayout>
  );
}

export default PreferencesReservation;
